import { useState, useEffect, FormEvent } from 'react';
import { apiService } from '../services/api';
import { LoginResponse, MFASetupResponse } from '../types/api.types';

interface MFASetupProps {
  sessionToken: string;
  onSuccess: (response: LoginResponse) => void;
  onBack: () => void;
  enrollMode?: boolean; // adopting an authenticator alongside email; completes the login
}

export default function MFASetup({ sessionToken, onSuccess, onBack, enrollMode = false }: MFASetupProps) {
  const [setupData, setSetupData] = useState<MFASetupResponse | null>(null);
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState('');
  const [showSecret, setShowSecret] = useState(false);

  useEffect(() => {
    const fetchSetupData = async () => {
      try {
        const data = await apiService.getMFASetup(sessionToken);
        setSetupData(data);
      } catch (err) {
        setError(apiService.getErrorMessage(err));
      } finally {
        setIsLoading(false);
      }
    };
    fetchSetupData();
  }, [sessionToken]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (!code.trim() || code.length !== 6) {
      setError('Please enter a 6-digit code from your authenticator app');
      return;
    }

    setIsVerifying(true);

    try {
      if (enrollMode) {
        // Adopting an authenticator: verifying the TOTP persists it and finishes the
        // login. No email confirmation follows — the mailbox was already proved.
        const response = await apiService.enrollVerifyOTP({
          session_token: setupData?.session_token || sessionToken,
          totp_code: code.trim(),
        });
        onSuccess(response);
      } else {
        const response = await apiService.confirmMFASetup({
          session_token: setupData?.session_token || sessionToken,
          totp_code: code.trim(),
        });
        onSuccess(response);
      }
    } catch (err) {
      setError(apiService.getErrorMessage(err));
    } finally {
      setIsVerifying(false);
    }
  };

  const copySecret = () => {
    if (setupData?.secret_key) {
      navigator.clipboard.writeText(setupData.secret_key);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="text-center text-gray-500">Loading MFA setup...</div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-2">Set Up Two-Factor Authentication</h2>
      <p className="text-gray-600 text-sm mb-6">
        Scan the QR code with your authenticator app (like Google Authenticator or Authy).
      </p>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
          {error}
        </div>
      )}

      {setupData && (
        <div className="space-y-4">
          {/* QR Code */}
          <div className="flex justify-center">
            <img
              src={setupData.qr_code_data}
              alt="MFA QR Code"
              className="border border-gray-200 rounded p-2"
              style={{ width: '200px', height: '200px' }}
            />
          </div>

          {/* Manual Entry */}
          <div className="text-center">
            <button
              type="button"
              onClick={() => setShowSecret(!showSecret)}
              className="text-sm text-blue-600 hover:underline"
            >
              {showSecret ? 'Hide' : 'Show'} manual entry key
            </button>
            {showSecret && setupData.secret_key && (
              <div className="mt-2 p-3 bg-gray-50 rounded">
                <p className="text-xs text-gray-500 mb-1">Secret Key</p>
                <div className="flex items-center justify-center gap-2">
                  <code className="text-sm font-mono">{setupData.secret_key}</code>
                  <button
                    type="button"
                    onClick={copySecret}
                    className="text-blue-600 hover:text-blue-800"
                    title="Copy to clipboard"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Verification Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-1">
                Enter the 6-digit code from your app
              </label>
              <input
                id="code"
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center text-2xl tracking-widest"
                placeholder="000000"
                maxLength={6}
                autoComplete="one-time-code"
                disabled={isVerifying}
              />
            </div>

            <button
              type="submit"
              disabled={isVerifying || code.length !== 6}
              className="w-full py-2 px-4 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isVerifying ? 'Verifying...' : enrollMode ? 'Verify & Continue' : 'Verify & Complete Setup'}
            </button>
          </form>
        </div>
      )}

      <div className="mt-4 text-center">
        <button
          type="button"
          onClick={onBack}
          className="text-sm text-gray-600 hover:underline"
        >
          Back to login
        </button>
      </div>
    </div>
  );
}
